import { z } from 'zod';
import { idSchema } from './schemas/common.ts';
import { staffMutationId } from './staff-portal.ts';

/**
 * Employee shift swaps (migration 0095). A cleaner asks a coworker — or anyone
 * available at the visit's location — to take one of their visits, optionally
 * trading it for one of the coworker's. The coworker answers first; then the
 * owner approves, unless the company lets same-team swaps approve themselves.
 */
export const SWAP_POLICIES = ['approval_required', 'auto_team', 'off'] as const;
export type SwapPolicy = (typeof SWAP_POLICIES)[number];
export const DEFAULT_SWAP_POLICY: SwapPolicy = 'approval_required';
export const DEFAULT_SWAP_CUTOFF_HOURS = 24;
/** A week. Past that, "no swaps within X hours" stops being a cutoff. */
export const MAX_SWAP_CUTOFF_HOURS = 168;

export const SWAP_STATUSES = ['pending_peer', 'pending_owner', 'approved', 'declined', 'canceled', 'expired'] as const;
export type SwapStatus = (typeof SWAP_STATUSES)[number];
export const OPEN_SWAP_STATUSES: readonly SwapStatus[] = ['pending_peer', 'pending_owner'];

export const swapPolicySchema = z.enum(SWAP_POLICIES);
export const swapCutoffHoursSchema = z.number().int().min(0).max(MAX_SWAP_CUTOFF_HOURS);

export const createSwapSchema = z.object({
  clientMutationId: staffMutationId,
  appointmentId: idSchema,
  /** Null asks anyone available at the visit's location. */
  targetUserId: idSchema.nullable().default(null),
  /** A visit of the coworker's to take in exchange; null gives the visit away. */
  offeredAppointmentId: idSchema.nullable().default(null),
  note: z.string().trim().max(1000).default(''),
});
export const respondSwapSchema = z.object({ response: z.enum(['accept', 'decline']) });
export const swapDecisionSchema = z.object({
  decision: z.enum(['approved', 'declined']),
  note: z.string().trim().max(1000).default(''),
});
export const swapEmailApprovalSchema = z.object({ token: z.string().min(1).max(4096) });

export interface SwapVisit {
  id: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  clientName: string;
  /** The neighbourhood, never the street address: enough to decide, nothing to share. */
  area: string | null;
  status: string;
}
export interface SwapPerson {
  id: string;
  name: string | null;
}
export interface SwapItem {
  id: string;
  status: SwapStatus;
  note: string;
  decisionNote: string | null;
  closeReason: string | null;
  createdAt: string;
  expiresAt: string;
  requester: SwapPerson;
  /** Null when the request went to anyone available. */
  target: SwapPerson | null;
  /** Who accepted, once someone has. */
  acceptedBy: SwapPerson | null;
  visit: SwapVisit;
  /** The coworker's visit in a trade; null for a give-away. */
  offeredVisit: SwapVisit | null;
  /** Signed-in person's relation to this swap. */
  own: boolean;
  incoming: boolean;
  canRespond: boolean;
  canCancel: boolean;
  canDecide: boolean;
}
export interface SwapListing {
  items: SwapItem[];
  /**
   * Every swap of the viewer's still waiting on someone (`pending_peer`,
   * `pending_owner`; up to 50, newest first), on the first page only, so none
   * is ever behind the history paging. May repeat entries of `items`; empty on
   * later pages.
   */
  open: SwapItem[];
  nextCursor: string | null;
  policy: SwapPolicy;
  cutoffHours: number;
  canWrite: boolean;
  canManage: boolean;
}
/**
 * Why a swap cannot go ahead. The code travels to the approval card, which
 * words it in the reader's language; the English is the API's error message.
 */
export const SWAP_PROBLEMS = {
  visit_unavailable: 'This visit is no longer available.',
  visit_closed: 'This visit was canceled or closed.',
  visit_started: 'This visit has already started.',
  visit_changed: 'The office changed this visit. Ask again if you still need a swap.',
  coworker_unavailable: 'That coworker can no longer take visits at this location.',
  requester_unavailable: 'The person asking for the swap can no longer take visits at this location.',
  requester_not_on_visit: 'The person asking is no longer on this visit.',
  coworker_on_visit: 'That coworker is already on this visit.',
  coworker_booked: 'That coworker is already booked at this time.',
  coworker_time_off: 'That coworker has approved time off at this time.',
  coworker_not_on_trade: 'That coworker is no longer on the visit offered in trade.',
  requester_on_trade: 'The person asking is already on the visit offered in trade.',
  requester_booked: 'The person asking is already booked at the time of the traded visit.',
  requester_time_off: 'The person asking has approved time off at the time of the traded visit.',
} as const;
export type SwapProblem = keyof typeof SWAP_PROBLEMS;

export interface SwapCrewChange {
  appointmentId: string;
  before: SwapPerson[];
  after: SwapPerson[];
}
/** What the office sees before approving: who goes where, and whether it still fits. */
export interface SwapReview {
  crews: SwapCrewChange[];
  /** Null when the swap can be approved as it stands; otherwise the reason it cannot. */
  conflict: SwapProblem | null;
  /** The recurring client's usual crew is broken by this swap. */
  sameCrewWarning: boolean;
}
/** A visit the coworker could trade, with when a swap for it would close. */
export interface SwapTradeVisit extends SwapVisit {
  /** The cutoff before the earlier of the two visits: a trade's own `expiresAt`. */
  closesAt: string;
}
export interface SwapCandidate {
  id: string;
  name: string;
  /**
   * Booked at the visit's time only on the visit they would trade, so they can
   * take it in a trade but not as a give-away (and are not among "anyone").
   */
  tradeOnly: boolean;
  /** The coworker's own upcoming visits at this location they could trade. */
  visits: SwapTradeVisit[];
}

/** The crew with one person replaced in place, so a lead stays the lead. */
export function swappedCrew(crew: readonly string[], leaving: string, joining: string): string[] | null {
  if (!crew.includes(leaving) || crew.includes(joining) || leaving === joining) return null;
  return crew.map((id) => (id === leaving ? joining : id));
}

/** The moment a swap stops being askable: the cutoff before the earlier visit starts. */
export function swapExpiresAt(startsAts: readonly string[], cutoffHours: number): string {
  const earliest = Math.min(...startsAts.map((value) => Date.parse(value)));
  return new Date(earliest - cutoffHours * 60 * 60 * 1000).toISOString();
}
